import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { Button } from '../ui/button';
import { atoAPI, errMsg, money } from '../../lib/atoAPI';
import { SimpleTable, SyntheticTag } from './AtoBits';

const useLoad = (fn, deps) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => fn().then(setData).catch((e) => toast.error(errMsg(e))), deps);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

const taskLink = (id) => (id ? <Link to={`/tasks?task=${id}`} className="text-primary hover:underline">Open task</Link> : null);

export const AtoAccounts = ({ clientId }) => {
  const [d] = useLoad(() => atoAPI.accounts(clientId ? { client_id: clientId } : {}), [clientId]);
  return (
    <div className="space-y-4">
      <SimpleTable testId="ato-accounts-table" rows={d?.latest || []} empty="No ATO account data yet."
        cols={[
          { k: 'account_type', label: 'Account', render: (r) => <span>{r.account_type} <SyntheticTag show={r.is_synthetic} /></span> },
          { k: 'balance', label: 'Balance', render: (r) => money(r.balance) },
          { k: 'change', label: 'Change since last sync', render: (r) => (r.change == null ? '—' : <span className={r.change > 0 ? 'text-rose-700' : 'text-emerald-700'}>{money(r.change)}</span>) },
          { k: 'payment_plan_ref', label: 'Payment plan' }, { k: 'checked_at', label: 'Last checked', render: (r) => r.checked_at?.slice(0, 16).replace('T', ' ') },
        ]} />
      <p className="text-xs text-muted-foreground">Balances use the latest snapshot per account only (no double counting). A positive balance is an amount owing.</p>
    </div>
  );
};

export const AtoLodgments = ({ clientId, canWrite }) => {
  const [d, load] = useLoad(() => atoAPI.lodgments(clientId ? { client_id: clientId } : {}), [clientId]);
  const link = async (r) => { try { const x = await atoAPI.linkTask(r.id); toast.success(x.created ? 'Task created' : 'Linked to existing task'); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <SimpleTable testId="ato-lodgments-table" rows={d?.items || []} empty="No lodgment obligations yet."
      cols={[
        { k: 'type', label: 'Obligation', render: (r) => <span>{r.type} <SyntheticTag show={r.is_synthetic} /></span> },
        { k: 'period', label: 'Period' }, { k: 'due_date', label: 'ATO due date' }, { k: 'status', label: 'ATO status' },
        { k: 'task_id', label: 'Internal task', render: (r) => taskLink(r.task_id) || (canWrite && r.status === 'Outstanding'
          ? <Button size="sm" variant="outline" onClick={() => link(r)} data-testid={`ato-link-task-${r.obligation_id}`}>Link / create task</Button> : '—') },
      ]} />
  );
};

export const AtoAsReports = ({ clientId, canWrite }) => {
  const [d] = useLoad(() => atoAPI.asReports(clientId ? { client_id: clientId } : {}), [clientId]);
  const csv = async () => { try { const b = await atoAPI.asCsv(clientId ? { client_id: clientId } : {}); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'as-reports.csv'; a.click(); } catch (e) { toast.error(errMsg(e)); } };
  const task = async (r) => { try { await atoAPI.asTask(r.id); toast.success('Reconciliation task created'); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3">
      <Button size="sm" variant="outline" onClick={csv} data-testid="ato-as-csv"><Download className="mr-1 h-4 w-4" />Download CSV</Button>
      <SimpleTable testId="ato-as-table" rows={d?.items || []} empty="No BAS/IAS history yet."
        cols={[
          { k: 'period', label: 'Period', render: (r) => <span>{r.period} <SyntheticTag show={r.is_synthetic} /></span> },
          { k: 'service_id', label: 'Report' }, { k: 'lodged', label: 'Lodged', render: (r) => (r.lodged ? 'Yes' : 'No') },
          { k: 'amount', label: 'Amount', render: (r) => money(r.amount) },
          { k: 'discrepancy', label: 'Reconciliation', render: (r) => (r.discrepancy ? <span className="text-rose-700">{r.discrepancy}</span> : <span className="text-emerald-700">Matches internal record</span>) },
          { k: 'act', label: '', render: (r) => (canWrite && r.discrepancy ? <Button size="sm" variant="ghost" onClick={() => task(r)} data-testid={`ato-as-task-${r.period_key}`}>Create task</Button> : null) },
        ]} />
    </div>
  );
};

export const AtoClientDetails = ({ clientId, canWrite }) => {
  const [d, load] = useLoad(() => atoAPI.clientDetails(clientId ? { client_id: clientId } : {}), [clientId]);
  const decide = async (r, ok) => { try { await atoAPI.decide(r.id, ok); toast.success(ok ? 'Client record updated' : 'Change rejected'); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <SimpleTable testId="ato-details-table" rows={d?.items || []} empty="No differences between ATO-held and practice client details."
      cols={[
        { k: 'field', label: 'Field', render: (r) => <span>{r.field} <SyntheticTag show={r.is_synthetic} /></span> },
        { k: 'current_value', label: 'Practice record' }, { k: 'ato_value', label: 'ATO-held' }, { k: 'recommended_action', label: 'Recommendation' },
        { k: 'status', label: 'Decision', render: (r) => (r.status === 'proposed' && canWrite ? (
          <span className="flex gap-1"><Button size="sm" onClick={() => decide(r, true)} data-testid={`ato-detail-accept-${r.field}`}>Accept</Button>
            <Button size="sm" variant="ghost" onClick={() => decide(r, false)} data-testid={`ato-detail-reject-${r.field}`}>Reject</Button></span>) : r.status) },
      ]} />
  );
};
